import React, { useEffect } from 'react';
import { useUI } from '@/context/UIContext';
import { ChevronDownIcon, CloseIcon } from '../../assets/icons';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// CN Utility: A simple version of clsx and tailwind-merge to combine class names.
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

// --- Card Component ---
interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
    ({ className, children, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("rounded-lg border bg-white text-darkGreen shadow-sm", className)}
            {...props}
        >
            {children}
        </div>
    ));
Card.displayName = "Card";

export const CardHeader = React.forwardRef<HTMLDivElement, CardProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props}>
            {children}
        </div>
    ));
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
    ({ className, children, ...props }, ref) => (
        <h3 ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props}>
            {children}
        </h3>
    ));
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
    ({ className, children, ...props }, ref) => (
        <p ref={ref} className={cn("text-sm text-gray-500", className)} {...props}>
            {children}
        </p>
    ));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<HTMLDivElement, CardProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("p-6 pt-0", className)} {...props}>
            {children}
        </div>
    ));
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<HTMLDivElement, CardProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props}>
            {children}
        </div>
    ));
CardFooter.displayName = "CardFooter";

// --- Button Component ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
}
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = 'primary', size = 'md', ...props }, ref) => {
        const variants = {
            primary: 'bg-primaryGreen text-white hover:bg-opacity-90',
            secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
            ghost: 'bg-transparent text-primaryGreen hover:bg-primaryGreen/10',
            danger: 'bg-red-600 text-white hover:bg-red-700',
        };
        const sizes = {
            sm: 'px-3 py-1.5 text-xs',
            md: 'px-4 py-2 text-sm',
            lg: 'px-6 py-2.5 text-base',
        };
        const baseClasses = 'inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
        return (
            <button
                ref={ref}
                className={cn(baseClasses, variants[variant], sizes[size], className)}
                {...props}
            />
        );
    }
);
Button.displayName = "Button";


export const ExpandButton: React.FC = () => {
    const { isGlobalExpanded, setIsGlobalExpanded } = useUI();
    return <button
        onClick={() => setIsGlobalExpanded(!isGlobalExpanded)}
        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-2 active:scale-[0.98]"
        title={isGlobalExpanded ? "Collapse Overview" : "Expand Table View"}
    >
        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-500 ${ isGlobalExpanded ? 'rotate-180 text-primaryGreen' : '' }`} />
        {isGlobalExpanded ? 'Minimize' : ''}
    </button>;
}


// --- Tabs Component ---
interface TabsProps {
    tabs: string[];
    activeTab: string;
    onTabClick: (tab: string) => void;
    className?: string;
    variant?: 'underline' | 'pills';
}
export const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onTabClick, className, variant = 'underline' }) => {
    if (variant === 'pills') {
        return (
            <div className={cn("flex space-x-1 border border-gray-200 rounded-lg p-1 bg-gray-50", className)}>
                {tabs.map(tab => (
                    <button
                        key={tab}
                        onClick={() => onTabClick(tab)}
                        className={cn(
                            'w-full px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                            activeTab === tab ? 'bg-primaryGreen text-white shadow' : 'text-gray-600 hover:bg-gray-100'
                        )}
                        aria-current={activeTab === tab ? 'page' : undefined}
                    >
                        {tab}
                    </button>
                ))}
            </div>
        )
    }

    return (
        <div className={cn("border-b border-gray-200", className)}>
            <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                {tabs.map(tab => (
                    <button
                        key={tab}
                        onClick={() => onTabClick(tab)}
                        className={cn(
                            'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm transition-colors',
                            activeTab === tab
                                ? 'border-primaryGreen text-primaryGreen'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        )}
                        aria-current={activeTab === tab ? 'page' : undefined}
                    >
                        {tab}
                    </button>
                ))}
            </nav>
        </div>
    );
}


// --- Modal Component ---
interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title: string;
    className?: string;
    dialogClassName?: string;
    hideHeader?: boolean;
    variant?: 'standard' | 'premium';
}
export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, className, dialogClassName, hideHeader, variant = 'standard' }) => {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isPremium = variant === 'premium';

    return (
        <div
            className={cn(
                "fixed inset-0 flex items-center justify-center p-4 md:p-8 z-[150] transition-all duration-500",
                isPremium ? "bg-darkGreen/12 backdrop-blur-sm" : "bg-black/45 backdrop-blur-[1px]",
                dialogClassName
            )}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onClick={onClose}
        >
            <div
                className={cn(
                    "shadow-2xl w-full max-h-[90vh] max-w-[calc(100vw-2rem)] flex flex-col relative overflow-hidden sm:w-auto sm:max-w-[80vw]",
                    isPremium ? "bg-white rounded-xl border border-gray-200/70" : "bg-white rounded-xl",
                    className
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {!hideHeader && (
                    <div className="flex items-center justify-between gap-4 p-4 border-b border-gray-100 bg-gray-50/30 flex-shrink-0 sm:p-6">
                        <h2 id="modal-title" className="min-w-0 text-lg font-black text-darkGreen tracking-tight uppercase font-outfit sm:text-xl">{title}</h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-darkGreen" aria-label="Close">
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>
                )}
                {/* The child will be responsible for its own scrolling and footer */}
                {/* <div className="overflow-y-auto flex-grow">{children}</div> */}
                <div className="flex-grow min-h-0 flex flex-col h-full overflow-hidden">
                    {children}
                </div>
            </div>
        </div>
    );
};
